import { useCallback, useEffect, useState } from "react";
import { useTheme, Grid, Chip, MenuItem, Menu } from "@mui/material";
import { useIntl } from "react-intl";

import AddIcon from "@mui/icons-material/Add";

import { CodeNameValue, DisplayZoneValue } from "./types";
import useEditedStyles from "../../../../hooks/useEditedStyles";
import HTChip from "../HTChip";

const SelectedPartitions = ({
  label,
  items,
  onChange,
  onAllChange,
  value,
  saved,
  disabled,
}: {
  label: string;
  items: (CodeNameValue & { color?: string })[];
  onChange: (v: boolean, index: number) => void;
  onAllChange?: (v: boolean[]) => void;
  value: boolean[] | null;
  saved: boolean[] | null;
  disabled?: boolean;
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedIndexes, setSelectedIndexes] = useState<number[] | null>(null);
  const open = Boolean(anchorEl);

  const { formatMessage } = useIntl();
  const theme = useTheme();
  const editedStyle = useEditedStyles();

  useEffect(() => {
    if (!value) {
      setSelectedIndexes(null);
      return;
    }
    setSelectedIndexes(
      value
        .map((item, itemIndex) => ({ item, itemIndex }))
        .filter(({ itemIndex }) => value[itemIndex])
        .map(({ itemIndex }) => itemIndex)
    );
  }, [value, theme]);

  const handleClick = (event: React.MouseEvent<HTMLDivElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const toggleItem = useCallback(
    (index: number) => {
      if (!value) {
        return;
      }
      value[index] = !value[index];
      setSelectedIndexes(
        items
          .map((item, itemIndex) => ({ item, itemIndex }))
          .filter(({ itemIndex }) => value[itemIndex])
          .map(({ itemIndex }) => itemIndex)
      );
      onChange(value[index], index);
    },
    [value, items, onChange]
  );

  const selectAll = useCallback(() => {
    if (!value || !onAllChange) {
      return;
    }
    setSelectedIndexes(Array.from({ length: items.length }).map((_, i) => i));
    value.forEach((_, i) => (value[i] = true));
    onAllChange(value);
  }, [value, items, onAllChange]);

  if (!selectedIndexes) {
    return null;
  }

  const partitionObjects = items
    .map((partition, partitionIndex) => ({ partition, partitionIndex }))
    .filter(({ partitionIndex }) => selectedIndexes.includes(partitionIndex));

  return (
    <Grid container spacing={1} sx={{ padding: 1 }}>
      <Grid item>
        <Chip
          label={label}
          onClick={handleClick}
          deleteIcon={<AddIcon />}
          onDelete={handleClick}
          disabled={selectedIndexes.length === items.length || disabled}
          className={saved && saved.filter((value, index) => value && !selectedIndexes.includes(index)).length > 0 ? editedStyle.withHover : ""}
        />
        <Menu
          id="basic-menu"
          anchorEl={anchorEl}
          open={open}
          onClose={handleClose}
          MenuListProps={{
            "aria-labelledby": "basic-button",
          }}
        >
          {onAllChange && (
            <MenuItem
              key={`item-al`}
              sx={{ fontStyle: "italic" }}
              onClick={() => {
                handleClose();
                selectAll();
              }}
            >
              {formatMessage({ defaultMessage: "Add all" })}
            </MenuItem>
          )}
          {items &&
            selectedIndexes &&
            items
              .map((item, itemIndex) => ({ item, itemIndex }))
              .filter(({ item, itemIndex }) => !selectedIndexes.includes(itemIndex) && !(item as unknown as DisplayZoneValue).disabled)
              .map(({ item, itemIndex }) => (
                <MenuItem
                  key={`item-${item.uuid}`}
                  className={saved && saved[itemIndex] ? editedStyle.withHover : ""}
                  disabled={item.deleted}
                  onClick={() => {
                    handleClose();
                    toggleItem(itemIndex);
                  }}
                >
                  {[item.deleted && `${formatMessage({ defaultMessage: "deleted" }).toUpperCase()} |`, item.code, item.name].filter((i) => !!i).join(" ")}
                </MenuItem>
              ))}
        </Menu>
      </Grid>
      {partitionObjects.map(({ partition, partitionIndex }) => (
        <Grid item key={`item-${partition.uuid}`}>
          <HTChip
            label={[partition.deleted && `${formatMessage({ defaultMessage: "deleted" }).toUpperCase()} |`, partition.name].filter((i) => !!i).join(" ")}
            code={partition.code}
            onDelete={disabled ? undefined : () => toggleItem(partitionIndex)}
            chipColor={partition.color}
            className={!saved?.[partitionIndex] || partition.deleted ? editedStyle.background : ""}
          />
        </Grid>
      ))}
    </Grid>
  );
};

export default SelectedPartitions;
